import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import moment from 'moment';
import { 
  Modal, 
  Table, 
  Button, 
  Form, 
  Input, 
  Select, 
  message, 
  Spin,
  Alert,
  Space,
  Tag,
  Popconfirm,
  Row,
  Col,
  Typography,
  Tooltip,
  Checkbox
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  MedicineBoxOutlined,
  SearchOutlined 
} from '@ant-design/icons';
import MomentDatePicker from './MomentDatePicker.tsx';
import { medicationRoutes, medicationFrequencies } from '../utils/medicationOptions';

const { Option } = Select;
const { Text } = Typography;
const { TextArea } = Input;

const MedicationManagementModal = ({
  visible,
  onCancel,
  patientId,
  onMedicationsUpdate
}) => {
  const [medications, setMedications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Add/Edit Modal states
  const [isAddEditModalVisible, setIsAddEditModalVisible] = useState(false);
  const [editingMedication, setEditingMedication] = useState(null);
  const [form] = Form.useForm();
  
  // Search functionality
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isCustomMedication, setIsCustomMedication] = useState(false);

  // Other frequency functionality
  const [isOtherFrequency, setIsOtherFrequency] = useState(false);

  // Fetch medications
  const fetchMedications = useCallback(async () => {
    if (!patientId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`/patients/${patientId}/medications?status=Active`);
      setMedications(response.data.data.medications || []);
    } catch (err) {
      console.error('Error fetching medications:', err);
      setError('Failed to load medications');
      setMedications([]);
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  // Load medications when modal opens
  useEffect(() => {
    if (visible && patientId) {
      fetchMedications();
    }
  }, [visible, patientId, fetchMedications]);

  // Search medications using RxNav API
  const searchMedications = useCallback(async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 3) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await axios.get('/medications/search', {
        params: { term: searchTerm }
      });
      setSearchResults(response.data.data.medications || []);
    } catch (err) {
      console.error('Error searching medications:', err);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Debounced search
  const debouncedSearch = useCallback(
    debounce(searchMedications, 300),
    [searchMedications]
  );

  // Handle medication selection from search
  const handleMedicationSelect = async (value, option) => {
    try {
      form.setFieldsValue({ medicationName: value });
      
      // Try to get detailed information from the API
      if (option.key && !option.key.startsWith('popular_')) {
        try {
          const response = await axios.get(`/medications/details/${option.key}`);
          const details = response.data.data.details;
          
          form.setFieldsValue({
            dosage: details.strength || '',
            route: details.doseForm || ''
          });
        } catch (error) {
          console.warn('Failed to fetch medication details:', error);
        }
      }
    } catch (error) {
      console.error('Error handling medication selection:', error);
    }
  };

  // Show Add/Edit Modal
  const showAddEditModal = (medication = null) => {
    setEditingMedication(medication);
    setIsCustomMedication(false);
    setSearchResults([]);

    if (medication) {
      // Determine if frequency is 'Other' for editing
      const standardFrequencies = medicationFrequencies.filter(f => f !== 'Other');
      const isOtherFreq = !standardFrequencies.includes(medication.frequency);
      setIsOtherFrequency(isOtherFreq);

      form.setFieldsValue({
        ...medication,
        startDate: medication.startDate ? moment(medication.startDate) : null,
        endDate: medication.endDate ? moment(medication.endDate) : null,
        frequency: isOtherFreq ? 'Other' : medication.frequency, // Set dropdown to 'Other' if needed
        otherFrequency: isOtherFreq ? medication.frequency : undefined, // Populate the text input if 'Other'
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        status: 'Active',
        isPrn: false
      });
      setIsOtherFrequency(false); // Reset other frequency state for new medication
    }

    setIsAddEditModalVisible(true);
  };

  // Handle Add/Edit Cancel
  const handleAddEditCancel = () => {
    setIsAddEditModalVisible(false);
    setEditingMedication(null);
    form.resetFields();
    setSearchResults([]);
    setIsOtherFrequency(false); // Reset other frequency state
  };

  // Handle Add/Edit Submit
  const handleAddEditSubmit = async (values) => {
    setSubmitLoading(true);

    try {
      const payload = {
        ...values,
        patient: patientId,
        startDate: values.startDate ? values.startDate.toDate() : null,
        endDate: values.endDate ? values.endDate.toDate() : null,
        frequency: values.frequency === 'Other' ? values.otherFrequency : values.frequency, // Use specified frequency if 'Other'
        isCustomMedication: isCustomMedication,
        medicationSource: isCustomMedication ? 'custom' : 'rxnav'
      };

      // Remove temporary fields not in the backend model
      delete payload.otherFrequency;

      if (editingMedication) {
        // Update existing medication
        await axios.patch(`/medications/${editingMedication._id}`, payload);
        message.success('Medication updated successfully!');
      } else {
        // Add new medication
        await axios.post(`/patients/${patientId}/medications`, payload);
        message.success('Medication added successfully!');
      }

      handleAddEditCancel();
      fetchMedications();
      
      // Notify parent component
      if (onMedicationsUpdate) {
        onMedicationsUpdate();
      }
    } catch (err) {
      console.error('Error saving medication:', err);
      message.error(err.response?.data?.message || 'Failed to save medication');
    } finally {
      setSubmitLoading(false);
    }
  };

  // Handle Delete Medication
  const handleDeleteMedication = async (medicationId) => {
    try {
      await axios.delete(`/medications/${medicationId}`);
      message.success('Medication deleted successfully!');
      fetchMedications();
      
      // Notify parent component
      if (onMedicationsUpdate) {
        onMedicationsUpdate();
      }
    } catch (err) {
      console.error('Error deleting medication:', err);
      message.error('Failed to delete medication');
    }
  };

  // Handle Status Update
  const handleStatusUpdate = async (medicationId, newStatus) => {
    try {
      await axios.patch(`/medications/${medicationId}`, { status: newStatus });
      message.success(`Medication status updated to ${newStatus}`);
      fetchMedications();
      
      // Notify parent component
      if (onMedicationsUpdate) {
        onMedicationsUpdate();
      }
    } catch (err) {
      console.error('Error updating medication status:', err);
      message.error('Failed to update medication status');
    }
  };

  // Table columns
  const columns = [
    {
      title: 'Medication',
      dataIndex: 'medicationName',
      key: 'medicationName',
      render: (text, record) => (
        <div>
          <Text strong style={{ color: '#1890ff' }}>{text}</Text>
          {record.isPrn && <Tag color="orange" style={{ marginLeft: 8 }}>PRN</Tag>}
          <Tag color={record.status === 'Active' ? 'green' : 'default'} style={{ marginLeft: 4 }}>
            {record.status}
          </Tag>
        </div>
      )
    },
    {
      title: 'Dosage',
      dataIndex: 'dosage',
      key: 'dosage'
    },
    {
      title: 'Frequency',
      dataIndex: 'frequency',
      key: 'frequency'
    },
    {
      title: 'Route',
      dataIndex: 'route',
      key: 'route'
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => showAddEditModal(record)}
              size="small"
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Popconfirm
              title="Are you sure you want to delete this medication?"
              onConfirm={() => handleDeleteMedication(record._id)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                size="small"
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      )
    }
  ];

  return (
    <>
      <Modal
        title={
          <Space>
            <MedicineBoxOutlined />
            Manage Medications
          </Space>
        }
        open={visible}
        onCancel={onCancel}
        footer={[
          <Button key="close" onClick={onCancel}>
            Close
          </Button>
        ]}
        width={900}
        destroyOnClose
      >
        <div style={{ marginBottom: 16 }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Text type="secondary">
                Manage active medications for this patient
              </Text>
            </Col>
            <Col>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => showAddEditModal()}
              >
                Add Medication
              </Button>
            </Col>
          </Row>
        </div>

        {error && (
          <Alert 
            message={error} 
            type="error" 
            showIcon 
            style={{ marginBottom: 16 }} 
          />
        )}

        <Table
          columns={columns}
          dataSource={medications}
          rowKey="_id"
          loading={loading}
          pagination={false}
          size="small"
          scroll={{ y: 400 }}
        />
      </Modal>

      {/* Add/Edit Medication Modal */}
      <Modal
        title={editingMedication ? 'Edit Medication' : 'Add New Medication'}
        open={isAddEditModalVisible}
        onCancel={handleAddEditCancel}
        footer={null}
        destroyOnClose
        width={600}
      >
        <Spin spinning={submitLoading}>
          <Form form={form} layout="vertical" onFinish={handleAddEditSubmit}>
            <Form.Item
              name="medicationName"
              label={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Medication Name</span>
                  <Button
                    type="link"
                    size="small"
                    onClick={() => setIsCustomMedication(!isCustomMedication)}
                    style={{ padding: 0, height: 'auto' }}
                  >
                    {isCustomMedication ? 'Switch to Search' : 'Switch to Custom'}
                  </Button>
                </div>
              }
              rules={[{ required: true, message: 'Medication name is required' }]}
            >
              {isCustomMedication ? (
                <Input placeholder="Enter custom medication name" />
              ) : (
                <Select
                  showSearch
                  placeholder="Search for medication..."
                  onSearch={debouncedSearch}
                  onSelect={handleMedicationSelect}
                  loading={isSearching}
                  filterOption={false}
                  notFoundContent={isSearching ? <Spin size="small" /> : null}
                >
                  {searchResults.map(med => (
                    <Option key={med.rxcui} value={med.displayName}>
                      {med.displayName}
                    </Option>
                  ))}
                </Select>
              )}
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="dosage"
                  label="Dosage"
                  rules={[{ required: true, message: 'Dosage is required' }]}
                >
                  <Input placeholder="e.g., 10mg, 5ml" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="frequency"
                  label="Frequency"
                  rules={[{ required: true, message: 'Frequency is required' }]}
                >
                  <Select
                    placeholder="Select frequency"
                    onChange={(value) => setIsOtherFrequency(value === 'Other')}
                  >
                    {medicationFrequencies.map(freq => (
                      <Option key={freq} value={freq}>{freq}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            {/* Conditional "Specify Frequency" Input */}
            {isOtherFrequency && (
              <Form.Item
                name="otherFrequency"
                label="Specify Frequency"
                rules={[{ required: true, message: 'Please specify the frequency' }]}
              >
                <Input placeholder="Enter custom frequency (e.g., Every 3 days, Twice weekly)" />
              </Form.Item>
            )}

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="route"
                  label="Route"
                  rules={[{ required: true, message: 'Route is required' }]}
                >
                  <Select placeholder="Select route">
                    {medicationRoutes.map(route => (
                      <Option key={route} value={route}>{route}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="status"
                  label="Status"
                  rules={[{ required: true, message: 'Status is required' }]}
                >
                  <Select>
                    <Option value="Active">Active</Option>
                    <Option value="Inactive">Inactive</Option>
                    <Option value="On Hold">On Hold</Option>
                    <Option value="Discontinued">Discontinued</Option>
                    <Option value="Pending">Pending</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="startDate"
                  label="Start Date"
                >
                  <MomentDatePicker
                    style={{ width: '100%' }}
                    format="YYYY-MM-DD"
                    placeholder="Select start date"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="endDate"
                  label="End Date"
                >
                  <MomentDatePicker
                    style={{ width: '100%' }}
                    format="YYYY-MM-DD"
                    placeholder="Select end date"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="isPrn"
              valuePropName="checked"
            >
              <Checkbox>PRN (As Needed)</Checkbox>
            </Form.Item>

            <Form.Item
              name="prnIndication"
              label="PRN Indication"
              dependencies={['isPrn']}
            >
              {({ getFieldValue }) =>
                getFieldValue('isPrn') ? (
                  <Input placeholder="Reason for PRN use" />
                ) : null
              }
            </Form.Item>

            <Form.Item
              name="notes"
              label="Notes"
            >
              <TextArea
                rows={3}
                placeholder="Additional notes or instructions..."
                maxLength={500}
                showCount
              />
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit" loading={submitLoading}>
                {editingMedication ? 'Update Medication' : 'Add Medication'}
              </Button>
              <Button style={{ marginLeft: 8 }} onClick={handleAddEditCancel}>
                Cancel
              </Button>
            </Form.Item>
          </Form>
        </Spin>
      </Modal>
    </>
  );
};

// Debounce utility function
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export default MedicationManagementModal;
